import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';
import { articles } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    fetch('https://readdy.ai/api/form/d9sq27dlbrt1qcgaj840', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = null; }
        if (res.ok && parsed?.code === 'OK') {
          setFormStatus('success');
          form.reset();
        } else {
          const msg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || text || '提交失败';
          setFormError(msg.includes('spam') ? '提交失败，请稍后再试' : msg);
          setFormStatus('error');
        }
      })
      .catch(() => {
        setFormError('网络错误，请稍后重试');
        setFormStatus('error');
      });
  };

  const articleCount = articles.length;
  const categoryCount = 6;
  const featuredCount = articles.filter((a) => a.featured).length;

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              关于我们
            </h1>
            <p className="text-sm text-foreground-600 max-w-xl">
              了解P站视频全攻略背后的故事与使命
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <section className="py-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              品牌介绍
            </h2>
            <div className="prose-content space-y-4">
              <p className="text-foreground-700 leading-relaxed">
                P站视频全攻略成立于2025年，是国内最早专注于P站视频使用教程的在线平台之一。我们致力于为P站视频用户提供全面、专业、易懂的使用指南，帮助每一位用户充分发挥平台的功能价值。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                从最基础的账号注册与界面导航，到高级搜索语法与批量下载技巧，我们的教程覆盖了P站视频使用的每一个环节。无论你是刚刚接触P站视频的新手，还是已经使用多年的老用户，都能在这里找到有价值的参考内容。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                截至2026年，我们已发布超过{articleCount}篇原创教程文章，建立了{categoryCount}大分类体系，累计帮助超过50万位用户提升了P站视频的使用效率。我们的内容被多家科技媒体和社区推荐，获得了广泛的好评与认可。
              </p>
            </div>
          </section>

          <section className="py-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              我们的使命
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  icon: 'ri-guide-line',
                  title: '降低使用门槛',
                  desc: '用通俗易懂的语言和循序渐进的教程，让每位用户都能快速上手P站视频的各项功能。',
                },
                {
                  icon: 'ri-lightbulb-line',
                  title: '挖掘隐藏技巧',
                  desc: '持续探索和分享P站视频的进阶用法与隐藏功能，帮助用户释放平台的全部潜力。',
                },
                {
                  icon: 'ri-group-line',
                  title: '构建用户社区',
                  desc: '打造一个友好互助的学习社区，让P站视频用户之间可以交流经验、分享心得。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              平台数据
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              我们的成长离不开每一位用户的支持
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: `${articleCount}+`, label: '原创教程', accent: true },
                { value: `${categoryCount}`, label: '分类体系', accent: false },
                { value: `${featuredCount}`, label: '热门文章', accent: false },
                { value: '50万+', label: '累计读者', accent: true },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className={`p-5 rounded-lg text-center ${
                    stat.accent
                      ? 'bg-accent-100/70 border border-accent-200'
                      : 'bg-background-50 border border-background-200/70'
                  }`}
                >
                  <div className={`text-2xl font-heading font-bold ${
                    stat.accent ? 'text-accent-700' : 'text-primary-600'
                  }`}>
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">
                        {t.name}
                      </div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs text-primary-500"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t.content}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              有任何问题、建议或合作意向，欢迎通过以下表单与我们联系
            </p>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="max-w-lg space-y-4"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1">
                  姓名
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1">
                  邮箱
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500字</p>
              </div>

              <div className="antispam-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                提交留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-accent-600 mt-2">感谢您的留言，我们会尽快回复！</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 mt-2">{formError || '提交失败，请稍后重试'}</p>
              )}
            </form>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于P站视频全攻略',
            description: '了解P站视频全攻略的品牌故事、使命与团队，致力于为用户提供最全面的P站视频使用教程。',
            mainEntity: {
              '@type': 'Organization',
              name: 'P站视频全攻略',
              description: '国内最早专注于P站视频使用教程的在线平台之一，已发布多篇原创教程，累计帮助超过50万位用户。',
              foundingDate: '2025',
            },
          }),
        }}
      />
    </>
  );
}